import React, { Component } from 'react';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { getTestAttributes } from '@common/utils/tests';
import isEmpty from 'lodash/isEmpty';

import { Flex } from '@common/components/Flex';
import { MessageBox } from '@common/components/MessageBox';
import Tree, { getItem } from '@common/components/Tree';
import { FolderCreating } from './components/folder-creating';
import { InfoMessage } from '@common/components/info-message';
import { Loading } from '../loading';
import { ErrorMessage } from '../error-message';
import { FormControl } from '@common/components/form-control';
import { Button } from '@common/components/Button';

import './FormatManagement.styles.scss';

export const FORMAT_MANAGEMENT_MODE = {
    save: 'save',
    load: 'load',
};

const MODULE_NAME = 'FORMAT_MANAGEMENT';

export default class FormatManagement extends Component {
    static propTypes = {
        formats: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
        })),
        deleteFormatBtnProps: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string,
        })),
        submitBtnProps: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string,
        })),
        deleteFormatBtnProps: PropTypes.shape({
            text: PropTypes.string,
        }),
        submitBtnProps: PropTypes.shape({
            text: PropTypes.string,
        }),
        folders: PropTypes.array,
        title: PropTypes.string,
        close: PropTypes.func.isRequired,
        getFolders: PropTypes.func.isRequired,
        getFormats: PropTypes.func,
        isAllowedToSubmit: PropTypes.func,
        selectedFormat: PropTypes.object,
        selectedFolder: PropTypes.object,
        showFormatNameHeader: PropTypes.bool,
        formatNameInputLabel: PropTypes.string,
        showDescriptionInput: PropTypes.bool,
    };

    static defaultProps = {
        formats: [],
        folders: [],
        mode: FORMAT_MANAGEMENT_MODE.load,
        formatsTableColumns: [],
        showFormatNameHeader: true
    };

    state = {
        messageProps: {
            header: '',
            text: '',
            show: false,
            confirmText: 'Ok',
            cancelText: '',
            size: 'small',
            onCustomOk: () => {},
        },
        loadingFolders: false,
        formatName: '',
        formatDescription: '',
        selectedFolderId: null,
        selectedFormatId: null,
        isFolderCreatingOpened: false,
    };

    get tableConfig() {
        return {
            columns: [
                { label: I18n.t('Format Name'), field: 'name' },
                ...this.props.formatsTableColumns,
            ],
        };
    }

    loadFolders = async () => {
        try {
            const { getFolders } = this.props;

            this.setState((x) => ({
                ...x,
                loadingFolders: true,
            }));

            await getFolders();
        } catch (err) {
            console.error(err);
            this.showErrorMessage(I18n.t('Failed to get folders'));
        } finally {
            this.setState((x) => ({
                ...x,
                loadingFolders: false,
            }));
        }
    }

    initialize = async () => {
        const { selectedFolder, selectedFormat } = this.props;

        try {
            await this.loadFolders();

            if (selectedFolder || (selectedFolder && selectedFormat)) {
                this.initializeDefaultValues();
            }
        } catch (err) {
            console.error(err);
            this.showErrorMessage(err?.entity || I18n.t('Failed to load folders'));
        }
    }

    componentDidMount() {
        this.initialize();  
    }

    initializeDefaultValues = async () => {
        const { selectedFolder, selectedFormat } = this.props;

        if (selectedFolder) {
            await this.handleFolderChange(selectedFolder.id);

            if (selectedFormat) {
                this.setState((state) => ({
                    ...state,
                    selectedFormatId: selectedFormat.id,
                }));
            }
        }
    }

    handleFolderChange = async (id) => {
        if (id === this.state.selectedFolderId) {
            return;
        }

        this.setState((state) => ({
            ...state,
            selectedFolderId: id,
            selectedFormatId: null,
        }));

        const { getFormats } = this.props;

        try {
            await getFormats(id);
        } catch (err) {
            console.error(err);
            this.showErrorMessage(err?.entity || I18n.t('Failed to load formats'));
        }
    }

    handleFormatChange = (e) => {
        e.persist();

        const id = Number(e.currentTarget.dataset.id);

        this.setState((state) => ({
            ...state,
            selectedFormatId: id,
        }));
    }

    handleLoadFormatSubmit = async () => {
        const { onSubmit, formats, folders } = this.props;
        const { selectedFormatId, selectedFolderId } = this.state;
        const format = formats.find((x) => x.id == selectedFormatId);
        const folder = getItem(selectedFolderId, folders);

        try {
            await onSubmit(format, isEmpty(folder) ? undefined : folder);
        } catch (err) {
            console.error(err);
            this.showErrorMessage(err?.entity || I18n.t('Failed to load format'));
        }
    }

    handleSaveFormatSubmit = async () => {
        try {
            const { onSubmit, formats } = this.props;
            const { selectedFolderId, formatName, selectedFormatId } = this.state;
            const format = formats.find((x) => x.id == selectedFormatId);

            await onSubmit({ folderId: selectedFolderId, name: formatName }, { format });
        } catch (err) {
            console.error(err);
            this.showErrorMessage(err?.entity || I18n.t('Failed to create a format'));
        }
    }

    handleNewFolderClick = () => {
        this.setState((state) => ({
            ...state,
            isFolderCreatingOpened: true,
        }));
    }

    closeFolderCreating = () => {
        this.setState((state) => ({
            ...state,
            isFolderCreatingOpened: false,
        }));
    }

    handleFolderDelete = () => {
        this.setMessageProps({ 
            header: I18n.t('Confirmation Message'),
            content: <InfoMessage>{I18n.t('Are you sure you want to delete this folder? All formats in this folder will also be deleted')}</InfoMessage>,
            show: true,
            confirmText: I18n.t('Yes'),
            cancelText: I18n.t('No'),
            onCustomOk: async () => {
                try {
                    await this.props.onFolderDelete(this.state.selectedFolderId);
                    this.closeMessage();
                    this.setState((state) => ({
                        ...state,
                        selectedFolderId: null,
                        selectedFormatId: null,
                    }));

                    if (this.props.resetFormats) {
                        this.props.resetFormats();
                    }
                } catch (err) {
                    console.error(err);
                    this.showErrorMessage(err?.entity || I18n.t('Failed to delete folder'));
                }
            }
        });
    }

    showErrorMessage = (message = I18n.t('Operation failed')) => {
        this.setMessageProps({
            header: I18n.t('Operation canceled'),
            content: <ErrorMessage>{message}</ErrorMessage>,
            show: true,
            confirmText: 'Ok',
            onCustomOk: this.closeMessage,
        });
    }

    handleFormatDelete = () => {
        this.setMessageProps({ 
            header: I18n.t('Confirmation Message'),
            content: <InfoMessage>{I18n.t('Are you sure you want to delete this format?')}</InfoMessage>,
            show: true,
            confirmText: I18n.t('Yes'),
            cancelText: I18n.t('No'),
            onCustomOk: async () => {
                try {
                    await this.props.onFormatDelete(this.state.selectedFolderId, this.state.selectedFormatId);
                    this.closeMessage();
                    this.setState((state) => ({
                        ...state,
                        selectedFormatId: null,
                    }));
                } catch (err) {
                    console.error(err);
                    this.showErrorMessage(err?.entity || I18n.t('Failed to delete format'));
                }
            }
        });
    }

    handleFormatNameChange = (e) => {
        e.persist();

        this.setState((state) => ({
            ...state,
            formatName: e.target.value,
        }));
    }

    handleFormatNameBlur = (e) => {
        e.persist();

        this.setState((state) => ({
            ...state,
            formatName: e.target.value.trim(),
        }));
    }

    handleFormatNameBlur = (e) => {
        e.persist();

        this.setState((state) => ({
            ...state,
            formatName: e.target.value.trim(),
        }));
    }

    handleFormatDescriptionChange = (e) => {
        e.persist();

        this.setState((state) => ({
            ...state,
            formatDescription: e.target.value,
        }));
    }

    handleFormatDescriptionBlur = (e) => {
        e.persist();

        this.setState((state) => ({
            ...state,
            formatDescription: e.target.value.trim(),
        }));
    }

    handleFolderCreate = (...args) => {
        return this.props.onFolderCreate(...args).then(this.closeFolderCreating).catch((err) => {
            console.error(err);
            this.showErrorMessage(err?.entity || I18n.t('Failed to create folder'));
        });
    }

    setMessageProps = (props) => {
        this.setState((state) => ({
            ...state,
            messageProps: {
                size: 'small',
                ...props,
            },
        }));
    }

    closeMessage = () => {
        this.setMessageProps({ show: false });
    }

    get modeConfig() {
        const { formatName, selectedFolderId, selectedFormatId } = this.state;

        const config = {
            [FORMAT_MANAGEMENT_MODE.save]: {
                title: I18n.t('Save Format'),
                submitBtnText: I18n.t('Save'),
                submitBtnDisabled: !formatName || !selectedFolderId,
                onSubmit: this.handleSaveFormatSubmit,
            },
            [FORMAT_MANAGEMENT_MODE.load]: {
                title: I18n.t('Load Format'),
                submitBtnText: I18n.t('Load'),
                submitBtnDisabled: !selectedFormatId,
                onSubmit: this.handleLoadFormatSubmit,
            }
        };

        return config[this.props.mode];
    }

    render() {
        const { folders, close, formats, onFormatDelete, onFolderCreate, onFolderDelete, mode, title, deleteFormatBtnProps, submitBtnProps, isAllowedToSubmit, getId } = this.props;
        const { selectedFolderId, selectedFormatId, isFolderCreatingOpened, formatName } = this.state;
        const shouldRenderFormatName = [FORMAT_MANAGEMENT_MODE.save].includes(mode);

        return (
            <>
                {isFolderCreatingOpened && (
                    <FolderCreating
                        getId={{
                            folderName: getId?.creatingFolderName,
                            folder: getId?.creatingFolderItem,
                            collapseIcon: getId?.creatingFolderCollapseItem,
                            cancelBtn: getId?.creatingFolderCancelBtn,
                            createBtn: getId?.creatingFolderCreateBtn,
                        }}
                        close={this.closeFolderCreating}
                        folders={folders}
                        onSubmit={this.handleFolderCreate}
                    />
                )}
                 <MessageBox
                    hideMessageBox={this.closeMessage}
                    {...this.state.messageProps}
                />
                <Modal 
                    show
                    backdrop="static"
                    bsClass="bootstrap modal"
                    className="FormatManagement"
                    dialogClassName="FormatManagement__dialog"
                    animation={false}
                    onEscapeKeyDown={close}
                    onHide={close}
                >
                    <ModalHeader closeButton onHide={close} className="FormatManagement__dialog__header">
                        <ModalTitle id="load-fm-format-header">{title ?? this.modeConfig.title}</ModalTitle>
                    </ModalHeader>
                    <ModalBody>
                        <div className="FormatManagement__content">
                            {shouldRenderFormatName && (
                                <Flex className="FormatManagement__content__format-name" alignItems="center">
                                    <span className="FormatManagement__content__format-name__label">{I18n.t('Format Name')}</span>
                                    <FormControl className="FormatManagement__content__format-name__input" {...getTestAttributes(MODULE_NAME, 'format-name', 'input')} value={formatName} onChange={this.handleFormatNameChange} onBlur={this.handleFormatNameBlur} />
                                </Flex>
                            )}
                            <Flex className="FormatManagement__content__folders-fields">
                                {this.state.loadingFolders && (
                                    <Flex width="50%" justify="center" alignItems="center" className="FormatManagement__content__folders--loading">
                                        <Loading margin="20px" size={25} />
                                    </Flex>
                                )}
                                {!this.state.loadingFolders && !folders.length && (
                                    <Flex width="50%" justify="center" alignItems="center" className="FormatManagement__content__folders">
                                        <div className="FormatManagement__content__no-data-msg">{I18n.t('There are no folders')}</div>
                                    </Flex>
                                )}
                                {Boolean(folders.length) && (
                                    <Tree
                                        className="FormatManagement__content__folders"
                                        items={folders}
                                        value={selectedFolderId}
                                        onChange={this.handleFolderChange}
                                    />
                                )}
                                <div className="FormatManagement__content__formats-table">
                                  {this.props.showFormatNameHeader &&  (
                                    <Flex width="100%" justify="space-between">
                                        {this.tableConfig.columns.map(({ label, field }) => (
                                            <div key={field} id={`lbl-fm-format-${field}`} className="FormatManagement__content__formats-table__header-cell" style={{ width: `${100 / this.tableConfig.columns.length}%` }}>
                                                {label}
                                            </div>
                                        ))}
                                    </Flex>
                                  )}
                                    <div className="FormatManagement__content__formats-table__content">
                                        {formats.map((item, rowIndex) => (
                                            <Flex 
                                                key={item.id} 
                                                {...getTestAttributes(MODULE_NAME, 'format-table', 'row', rowIndex)}
                                                width="100%" 
                                                justify="space-between" 
                                                className={cx(
                                                    'FormatManagement__content__formats-table__content__row',
                                                    { 'FormatManagement__content__formats-table__content__row-selected': item.id === selectedFormatId },
                                                )}
                                                data-id={item.id}
                                                onClick={this.handleFormatChange}
                                            >
                                                {this.tableConfig.columns.map(({ field }, colIndex) => (
                                                    <div 
                                                        style={{ width: `${100 / this.tableConfig.columns.length}%` }}
                                                        key={field}
                                                        {...getTestAttributes({ id: getId?.field ? getId.field({ rowIndex, item, colIndex }) : item.id })}
                                                        className="FormatManagement__content__formats-table__content__cell"
                                                    >
                                                        {item[field]}
                                                    </div>
                                                ))}
                                            </Flex>
                                        ))}
                                    </div>
                                </div>
                            </Flex>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Flex justify="space-between">
                            <Flex>
                                {onFolderCreate && (
                                    <Button 
                                        {...(getId?.newFolderBtn ? getTestAttributes({ id: getId.newFolderBtn }) : getTestAttributes(MODULE_NAME, 'new-folder', 'btn'))}
                                        onClick={this.handleNewFolderClick}
                                    >
                                            {I18n.t('New Folder')}
                                    </Button>
                                )}
                                {onFolderDelete && (
                                    <Button 
                                        {...(getId?.deleteFolderBtn ? getTestAttributes({ id: getId.deleteFolderBtn }) : getTestAttributes(MODULE_NAME, 'delete-folder', 'btn'))}
                                        disabled={!selectedFolderId} 
                                        onClick={this.handleFolderDelete}
                                    >
                                        {I18n.t('Delete Folder')}
                                    </Button>
                                )}
                            </Flex>

                            {onFormatDelete && (
                                <Button 
                                    {...(getId?.deleteFormatBtn ? getTestAttributes({ id: getId.deleteFormatBtn }) : getTestAttributes(MODULE_NAME, 'delete-format', 'btn'))}
                                    className="FormatManagement__delete-btn" 
                                    onClick={this.handleFormatDelete}
                                    disabled={!selectedFormatId}
                                >
                                    {deleteFormatBtnProps?.text ?? I18n.t('Delete Format')}
                                </Button>
                            )}

                            <Flex>
                                <Button id="btn-fm-cancel" onClick={close}>{I18n.t('Cancel')}</Button>
                                <Button
                                    id="btn-fm-load"
                                    disabled={isAllowedToSubmit
                                        ? !isAllowedToSubmit({
                                            folderId: this.state.selectedFolderId,
                                            formatId: this.state.selectedFormatId,
                                            newFormatName: this.state.formatName
                                        })
                                        : this.modeConfig.submitBtnDisabled}
                                    className="btn-primary"
                                    onClick={this.modeConfig.onSubmit}
                                >
                                    {submitBtnProps?.text ?? this.modeConfig.submitBtnText}
                                </Button>
                            </Flex>
                        </Flex>
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}